/**
 * MyWatchesSection — lists all resources the user is watching.
 * Grouped by type with unwatch buttons and links to resources.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import * as watchApi from '../../api/watch-api';
import type { WatchItem } from '../../api/watch-api';

const TYPE_LABELS: Record<string, string> = {
  board: 'Board',
  card: 'Card',
  library: 'Library',
  page: 'Page',
};

const TYPE_COLORS: Record<string, string> = {
  board: 'bg-blue-100 text-blue-800',
  card: 'bg-green-100 text-green-800',
  library: 'bg-purple-100 text-purple-800',
  page: 'bg-amber-100 text-amber-800',
};

export function MyWatchesSection(): React.ReactElement {
  const navigate = useNavigate();
  const [watches, setWatches] = useState<WatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const result = await watchApi.listMyWatches();
        if (!cancelled) setWatches(result.data ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load watches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleUnwatch = useCallback(async (item: WatchItem) => {
    try {
      await watchApi.unwatch(item.resourceType, item.resourceId);
      setWatches((prev) => prev.filter((w) => !(w.resourceType === item.resourceType && w.resourceId === item.resourceId)));
    } catch {
      // Silent fail
    }
  }, []);

  const handleNavigate = useCallback((item: WatchItem) => {
    switch (item.resourceType) {
      case 'board':
        navigate(`/boards/${item.resourceId}`);
        break;
      case 'card':
        // Cards need boardId which we don't have in the watch record — navigate to boards list
        navigate('/boards');
        break;
      case 'library':
        navigate(`/pages/${item.resourceId}`);
        break;
      case 'page':
        navigate(`/pages`);
        break;
    }
  }, [navigate]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Resources you're watching. You'll be notified when changes happen.
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!loading && watches.length === 0 && (
        <p className="text-sm text-muted-foreground">Not watching anything yet. Use the eye icon on boards, cards, libraries, or pages to start watching.</p>
      )}

      {!loading && watches.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="Watched resources">
          {watches.map((item) => (
            <li key={`${item.resourceType}-${item.resourceId}`} className="flex items-center justify-between rounded-md border px-3 py-2">
              <button onClick={() => handleNavigate(item)} className="flex items-center gap-2 text-left min-w-0">
                <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded ${TYPE_COLORS[item.resourceType] ?? ''}`}>
                  {TYPE_LABELS[item.resourceType] ?? item.resourceType}
                </span>
                <span className="text-sm font-medium truncate">{item.resourceTitle || 'Untitled'}</span>
              </button>
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive shrink-0"
                onClick={() => handleUnwatch(item)}
              >
                Unwatch
              </Button>
            </li>
          ))}
        </ul>
      )}

      {watches.length > 0 && (
        <p className="text-xs text-muted-foreground">{watches.length} of 50 watch slots used</p>
      )}
    </div>
  );
}
